import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Share2, Users, Eye, Pencil, Loader2, ArrowDownLeft, ArrowUpRight, ShieldCheck } from 'lucide-react';
import { getFamilyForPatient, clearViewedFamily } from '../../redux/familySlice';
import { getFileURL } from '../../utils/fileURL';

// DEPENDENCIES — who this PERSON is connected to, from the outside.
//
// ONE FAMILY, NOT ONE PER HOSPITAL. Since one-patient-row (owner, 22 Sep 2026)
// a share edge joins two person rows and carries no tenant of its own
// (doc/one-patient-row.md §2), so this list is the same answer whichever
// hospital the operator was last looking at — and it exists even for a person
// no hospital has seen yet. That is why nothing here asks for a hospital any
// more and why the tab is never locked.
//
// WHY THIS IS NOT THE TENANT COMPONENT.
//
// Frontend-web's Dependencies tab is written for the person whose account it is:
// it calls getMyFamily() / getShares(), which answer "MY family" and "MY shares"
// for whoever holds the token, and its switch-profile control re-issues that
// token so the caller starts ACTING AS somebody else. Rendering it here would
// have shown a Super Admin their own (empty) family under a patient's name, and
// its one working button would have signed the operator in as a patient.
//
// An administrator must never switch INTO a patient's profile. Break-glass
// exists precisely so that reading clinical data costs an audit row with a
// reason on it; becoming the patient would route around that entirely. So
// switch-profile, add-secondary and split are all gone, and what is left is the
// same information read through getFamilyForPatient(patientId) — the endpoint
// built for staff looking at somebody else.
//
// TWO COLUMNS, NEVER ONE. Sharing has a direction: "who can open THEM" and "what
// THEY can open" are different facts about different records. Merging them would
// imply access is mutual, which is the exact thing the share model exists to
// stop being true.

const card = 'rounded-2xl border border-gray-200 bg-white';
const sectionHead = 'flex items-center gap-2 border-b border-gray-100 px-5 py-3.5';

const Avatar = ({ person, size = 'h-8 w-8' }) => (
    person?.image || person?.imagePath
        ? <img src={getFileURL(person.image || person.imagePath)} alt="" className={`${size} flex-shrink-0 rounded-full object-cover`} />
        : (
            <span className={`${size} flex flex-shrink-0 items-center justify-center rounded-full bg-[#4ABDBD]/15 text-xs font-bold text-[#3AA8A8]`}>
                {(person?.name || '?').charAt(0).toUpperCase()}
            </span>
        )
);

const PermissionChip = ({ permission, readLabel, writeLabel }) => (
    permission === 'readwrite' ? (
        <span className="inline-flex items-center gap-0.5 rounded bg-[#4ABDBD]/15 px-1 py-px text-[9px] font-semibold text-[#3AA8A8]">
            <Pencil className="h-2.5 w-2.5" />{writeLabel}
        </span>
    ) : (
        <span className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 py-px text-[9px] font-semibold text-gray-500">
            <Eye className="h-2.5 w-2.5" />{readLabel}
        </span>
    )
);

const PersonRow = ({ person, primaryLabel, secondaryLabel, readLabel, writeLabel }) => (
    <div className="flex items-center gap-2.5 rounded-xl border border-gray-200 p-2.5">
        <Avatar person={person} />
        <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-gray-800">{person.name || '—'}</span>
            <span className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-400">
                <span>{person.accountType === 'secondary' ? secondaryLabel : primaryLabel}</span>
                <PermissionChip permission={person.permission} readLabel={readLabel} writeLabel={writeLabel} />
            </span>
            {/* A SECONDARY has no phone or email of their own — they are reached
                through whoever manages them. Without this line a clinic ringing
                about a child's appointment sees a blank number and is stuck. */}
            {(person.phone || person.email) && (
                <span className="block truncate text-[10px] text-gray-400">
                    {[person.phone, person.email].filter(Boolean).join(' · ')}
                </span>
            )}
        </span>
    </div>
);

const DependenciesReadOnly = ({ patientId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { viewed, viewedLoading } = useSelector((s) => s.family || {});

    useEffect(() => {
        if (!patientId) return undefined;
        dispatch(getFamilyForPatient(patientId));
        // Clear on unmount, or the previous patient's links paint for a frame
        // under the next one's name.
        return () => { dispatch(clearViewedFamily()); };
    }, [dispatch, patientId]);

    const primaryLabel = t('family.primary', 'Primary');
    const secondaryLabel = t('family.secondary', 'Secondary');
    const readLabel = t('family.read_only', 'Read only');
    const writeLabel = t('family.read_write', 'Read & write');

    const managedBy = viewed?.managedBy || [];
    const theyCanOpen = viewed?.theyCanOpen || [];

    // No id at all can only mean the page has not resolved the person yet.
    if (!patientId) {
        return (
            <div className={`${card} p-6 text-center`} data-testid="moc-dep-no-person">
                <ShieldCheck className="mx-auto h-6 w-6 text-gray-300" />
                <p className="mt-2 text-sm font-semibold text-[#1a365d]">
                    {t('moc.dep_no_person', 'No patient selected')}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5">
            <section className={card} data-testid="moc-dep-sharing">
                <div className={sectionHead}>
                    <Share2 className="h-4 w-4 text-[#4ABDBD]" />
                    <h3 className="flex-1 text-sm font-bold text-[#1a365d]">{t('family.sharing', 'Sharing')}</h3>
                    <span className="text-[10px] font-semibold text-gray-400">
                        {t('moc.dep_everywhere', 'At every hospital')}
                    </span>
                </div>

                <p className="px-5 pt-3 text-[11px] leading-relaxed text-gray-500">
                    {t('moc.dep_note',
                        'The patient decides all of this themselves, once, for every hospital they visit. Shown here so the platform can see who reaches this profile — it cannot be changed from Super Admin, and an administrator can never switch into a patient’s profile.')}
                </p>

                <div className="grid grid-cols-1 gap-5 px-5 py-4 md:grid-cols-2">
                    <div className="flex min-w-0 flex-col gap-2">
                        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                            <ArrowUpRight className="h-3 w-3" />
                            {t('family.managed_by', 'Who can open this account')}
                        </p>
                        <p className="text-[10px] leading-relaxed text-gray-400">
                            {t('moc.dep_managed_by_note', 'For a secondary this is the person who manages them — and who a clinic should ring.')}
                        </p>
                        {viewedLoading && <Loader2 className="mx-auto h-4 w-4 animate-spin text-[#4ABDBD]" />}
                        {!viewedLoading && managedBy.length === 0 && (
                            <p className="rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                                {t('moc.dep_nobody_can_open', 'Nobody else can open this account.')}
                            </p>
                        )}
                        {managedBy.map((person) => (
                            <PersonRow
                                key={person.patientId || person.shareId}
                                person={person}
                                primaryLabel={primaryLabel}
                                secondaryLabel={secondaryLabel}
                                readLabel={readLabel}
                                writeLabel={writeLabel}
                            />
                        ))}
                    </div>

                    <div className="flex min-w-0 flex-col gap-2">
                        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                            <ArrowDownLeft className="h-3 w-3" />
                            {t('family.they_can_open', 'Accounts this patient can open')}
                        </p>
                        <p className="text-[10px] leading-relaxed text-gray-400">
                            {t('moc.dep_they_can_open_note', 'Their own secondary patients, plus anyone who has shared with them.')}
                        </p>
                        {viewedLoading && <Loader2 className="mx-auto h-4 w-4 animate-spin text-[#4ABDBD]" />}
                        {!viewedLoading && theyCanOpen.length === 0 && (
                            <p className="rounded-xl bg-gray-50 px-3 py-3 text-[11px] text-gray-500">
                                {t('moc.dep_opens_nothing', 'This patient cannot open anyone else’s account.')}
                            </p>
                        )}
                        {theyCanOpen.map((person) => (
                            <PersonRow
                                key={person.patientId || person.shareId}
                                person={person}
                                primaryLabel={primaryLabel}
                                secondaryLabel={secondaryLabel}
                                readLabel={readLabel}
                                writeLabel={writeLabel}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {viewed?.enabled === false && (
                <section className={`${card} p-5`} data-testid="moc-dep-disabled">
                    <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <p className="text-xs text-gray-500">
                            {t('moc.family_feature_off', 'Family accounts are switched off platform-wide, so no new links can be created.')}
                        </p>
                    </div>
                </section>
            )}
        </div>
    );
};

export default DependenciesReadOnly;
